if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Store timer so multiple clicks don't create multiple timers
        this.addedToCartTimer = null;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const loadingSpinner = this.querySelector('.loading__spinner');

        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');

        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );

          formData.append(
            'sections_url',
            window.location.pathname
          );

          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        const variantId = formData.get('id');
        const quantity =
          parseInt(formData.get('quantity')) || 1;

        const linesUpdateDeferred =
          this.createCartLinesUpdateEvent(
            variantId,
            quantity
          );

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())

          .then((response) => {
            /*
             * ------------------------------------------
             * ERROR / SOLD OUT
             * ------------------------------------------
             */

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors:
                  response.errors ||
                  response.description,
                message: response.message,
              });

              this.handleErrorMessage(
                response.description
              );

              this.dispatchCartErrorEvent(
                response.description ||
                  response.message,
                'INVALID'
              );

              linesUpdateDeferred?.reject(
                new Error(
                  response.description ||
                    response.message
                )
              );

              const soldOutMessage =
                this.submitButton.querySelector(
                  '.sold-out-message'
                );

              if (!soldOutMessage) return;

              this.submitButton.setAttribute(
                'aria-disabled',
                true
              );

              if (this.submitButtonText) {
                this.submitButtonText.classList.add(
                  'hidden'
                );
              }

              soldOutMessage.classList.remove(
                'hidden'
              );

              this.error = true;

              return;
            }

            /*
             * ------------------------------------------
             * NO CART DRAWER / CART NOTIFICATION
             * ------------------------------------------
             */

            if (!this.cart) {
              this.resolveCartLinesUpdate(
                linesUpdateDeferred
              );

              window.location =
                window.routes.cart_url;

              return;
            }

            this.resolveCartLinesUpdate(
              linesUpdateDeferred
            );

            /*
             * ------------------------------------------
             * CART UPDATE EVENT
             * ------------------------------------------
             */

            const startMarker =
              CartPerformance.createStartingMarker(
                'add:wait-for-subscribers'
              );

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });
            }

            this.error = false;

            /*
             * ------------------------------------------
             * QUICK ADD MODAL
             * ------------------------------------------
             */

            const quickAddModal =
              this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(
                          response
                        );
                      }
                    );

                    // Show success state after render
                    setTimeout(() => {
                      this.showAddedToCartState();
                    }, 100);
                  }, 0);
                },
                { once: true }
              );

              quickAddModal.hide(true);

            } else {

              /*
               * ------------------------------------------
               * NORMAL ADD TO CART
               * ------------------------------------------
               */

              CartPerformance.measure(
                'add:paint-updated-sections',
                () => {
                  this.cart.renderContents(
                    response
                  );
                }
              );

              // Wait until Shopify finishes rendering
              setTimeout(() => {
                this.showAddedToCartState();
              }, 500);
            }
          })

          .catch((e) => {
            console.error(e);

            this.dispatchCartErrorEvent(
              e.message || 'Network error',
              'SERVICE_UNAVAILABLE'
            );

            linesUpdateDeferred?.reject(e);
          })

          .finally(() => {
            this.submitButton.classList.remove(
              'loading'
            );

            if (
              this.cart &&
              this.cart.classList.contains(
                'is-empty'
              )
            ) {
              this.cart.classList.remove(
                'is-empty'
              );
            }

            if (!this.error) {
              this.submitButton.removeAttribute(
                'aria-disabled'
              );
            }

            const loadingSpinner =
              this.querySelector(
                '.loading__spinner'
              );

            if (loadingSpinner) {
              loadingSpinner.classList.add(
                'hidden'
              );
            }

            CartPerformance.measureFromEvent(
              'add:user-action',
              evt
            );
          });
      }

      /*
       * ==========================================
       * ADDED TO CART BUTTON STATE
       * ==========================================
       */

      showAddedToCartState() {
        // Clear previous timer
        if (this.addedToCartTimer) {
          clearTimeout(
            this.addedToCartTimer
          );
        }

        // Find the CURRENT button again
        // Shopify may have updated the DOM
        const button =
          this.querySelector(
            '[type="submit"]'
          );

        if (!button) return;

        const buttonText =
          button.querySelector('span');

        if (!buttonText) return;

        /*
         * Change:
         * Add to Cart
         * ↓
         * Added to Cart
         */

        buttonText.textContent =
          'Added to Cart';

        /*
         * Return after 2 seconds
         */

        this.addedToCartTimer =
          setTimeout(() => {

            // Find the CURRENT button again
            const currentButton =
              this.querySelector(
                '[type="submit"]'
              );

            if (!currentButton) return;

            const currentButtonText =
              currentButton.querySelector(
                'span'
              );

            if (!currentButtonText) return;

            currentButtonText.textContent =
              window.variantStrings?.addToCart ||
              'Add to Cart';

          }, 2000);
      }

      handleErrorMessage(
        errorMessage = false
      ) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }

      toggleSubmitButton(
        disable = true,
        text
      ) {
        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text) {
            this.submitButtonText.textContent =
              text;
          }

        } else {
          this.submitButton.removeAttribute(
            'disabled'
          );

          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(
        variantId,
        quantity
      ) {
        const {
          CartLinesUpdateEvent
        } = window.StandardEvents || {};

        if (!CartLinesUpdateEvent) {
          return null;
        }

        const deferred =
          CartLinesUpdateEvent.createPromise();

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [
              {
                merchandiseId: variantId,
                quantity,
              },
            ],
            promise: deferred.promise,
          })
        );

        return deferred;
      }

      resolveCartLinesUpdate(
        deferred
      ) {
        if (!deferred) return;

        const {
          CartLinesUpdateEvent
        } = window.StandardEvents || {};

        if (!CartLinesUpdateEvent) {
          return;
        }

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(
                `${routes.cart_url}.json`
              ).then((response) =>
                response.json()
              );

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) {
              return deferred.reject(
                new Error(
                  'Missing currency in cart response'
                )
              );
            }

            deferred.resolve({
              cart:
                CartLinesUpdateEvent.createCartFromAjaxResponse(
                  cart
                ),
            });
          })

          .catch((e) =>
            deferred.reject(e)
          );
      }

      dispatchCartErrorEvent(
        message,
        code
      ) {
        const {
          CartErrorEvent
        } = window.StandardEvents || {};

        if (!CartErrorEvent) return;

        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code,
          })
        );
      }

      get variantIdInput() {
        return this.form.querySelector(
          '[name=id]'
        );
      }
    }
  );
}